// src/lib/realtimeManager.js
//
// Hardening Sprint Track 1.3 — centralized realtime subscription manager.
//
// Before this each component spun up its own supabase.channel(...) and
// owned its own cleanup. The result was scattered subscriptions that
// either leaked on unmount, doubled up after an event change, or fired
// against stale state because the component had re-rendered.
//
// This manager is the single owner of every postgres_changes channel.
// Components call `subscribe(...)` and pass a callback; the manager
// holds the channel and unsubscribes on clear or when the event scope
// changes. authCleanup.clearAllUserState() calls clearAll() so a logout
// or account switch tears every listener down before the new user's
// data starts hydrating.
//
// ── Listener coverage audit (vs Hardening Sprint Track 1.3 spec) ──
//
// SPEC table          STATUS         OWNER (today)
// events              MISSING        — (no realtime; status changes
//                                       only land on the next mount)
// event_players       PARTIAL        IdentityCard (faction_id only)
// event_factions      MISSING        —
// bounties            COVERED        GameEvent + PlayerView
// kill_log            PARTIAL        foregroundNotify (for pushes only)
// fragos (=bounties)  COVERED        GameEvent + PlayerView
// frago_phases        MISSING        — (rework Phase 1 introduced; no
//                                       subscriber yet)
// frago_objectives    MISSING        —
// team_members        COVERED        PlayerView (user-scoped) +
//                                       IdentityCard
// mcs_nodes           MISSING        —
// mcs_node_members    PARTIAL        IdentityCard (user-scoped)
// event_checkpoints   MISSING        —
// event_pauses        MISSING        PlayerView (polls every 30s)
//
// Migration plan: each component that currently owns a channel migrates
// to realtimeManager.subscribe() in follow-up commits. The manager API
// is deliberately backward-compatible with the per-channel pattern so
// the migrations can land one at a time without flipping a switch.

import { supabase } from './supabaseClient'

// Each registered subscriber is keyed by an opaque string (the caller
// chooses one — typically a component name + table). The manager owns
// the underlying supabase channel; the caller gets a stable unsubscribe
// handle that survives event-scope changes.
const registry = new Map()
// id → { eventId, table, event, callback, channel }

let activeEventId = null

// Internal: build the channel for one registry entry. Each registry
// entry gets its own channel so removal is granular — replacing the
// "one channel, many .on() handlers" pattern that broke when any single
// handler had to be torn down.
function buildChannel(id, entry) {
  if (!entry.eventId) return null
  const channelName = `rt-${id}-${entry.eventId}`
  const filter = entry.filter || `event_id=eq.${entry.eventId}`
  const channel = supabase
    .channel(channelName)
    .on(
      'postgres_changes',
      { event: entry.event || '*', schema: 'public', table: entry.table, filter },
      (payload) => {
        try { entry.callback(payload) } catch (err) {
          console.warn('[realtimeManager] callback threw', { id, table: entry.table, err })
        }
      }
    )
    .subscribe()
  return channel
}

// Subscribe to a table's postgres_changes for the current event scope.
//
//   id        opaque subscriber id; reusing it replaces the previous sub
//   table     database table name
//   callback  fn(payload) — called on each row change
//   options:
//     event   'INSERT' | 'UPDATE' | 'DELETE' | '*' (default '*')
//     filter  override the default `event_id=eq.<scope>` filter — useful
//             for user-scoped subscriptions (e.g., team_members for the
//             current user, mcs_node_members for the current user)
//     eventId override the manager's current scope (rare; default uses
//             the eventId passed to setActiveEvent)
//
// Returns an unsubscribe function. Calling it is idempotent.
export function subscribe(id, table, callback, options = {}) {
  if (!id || !table || typeof callback !== 'function') return () => {}
  // If there's an existing entry under this id, tear it down first so
  // we don't leak a channel.
  const prev = registry.get(id)
  if (prev?.channel) {
    try { supabase.removeChannel(prev.channel) } catch { /* ignore */ }
  }
  const entry = {
    eventId: options.eventId !== undefined ? options.eventId : activeEventId,
    table,
    event: options.event || '*',
    filter: options.filter,
    callback,
    channel: null,
  }
  entry.channel = buildChannel(id, entry)
  registry.set(id, entry)
  return () => {
    const cur = registry.get(id)
    if (cur?.channel) {
      try { supabase.removeChannel(cur.channel) } catch { /* ignore */ }
    }
    registry.delete(id)
  }
}

// Pin the active event scope. Every registered subscription whose
// `eventId` was bound to the previous scope gets torn down + re-built
// against the new scope, so the manager keeps the same set of callbacks
// alive across event switches without callers re-registering.
export function setActiveEvent(eventId) {
  if (activeEventId === eventId) return
  activeEventId = eventId
  // Re-build every registry entry. Subscriptions whose options.eventId
  // was explicitly pinned (not derived from activeEventId) keep their
  // original scope — we detect that by storing the explicit override on
  // registration. Today every registration uses activeEventId, so this
  // is effectively a full refresh.
  for (const [id, entry] of registry.entries()) {
    if (entry.channel) {
      try { supabase.removeChannel(entry.channel) } catch { /* ignore */ }
    }
    entry.eventId = eventId
    entry.channel = buildChannel(id, entry)
  }
}

// Tear down every registered channel. Called by authCleanup on
// SIGNED_OUT / account switch. After this call the manager is empty
// and ready to accept new subscriptions for the next user.
export function clearAll() {
  for (const entry of registry.values()) {
    if (entry.channel) {
      try { supabase.removeChannel(entry.channel) } catch { /* ignore */ }
    }
  }
  registry.clear()
  activeEventId = null
}

// Debug helper — returns a snapshot of the current registry without
// exposing the live channel objects. Used by dev tooling + tests.
export function inspect() {
  return [...registry.entries()].map(([id, e]) => ({
    id, table: e.table, event: e.event, filter: e.filter, eventId: e.eventId,
  }))
}

export default { subscribe, setActiveEvent, clearAll, inspect }
